import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { UserRole, AuthState } from '../types/auth'
import * as authService from '../services/authService'

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  hasRole: (roles: UserRole[]) => boolean
  isLoading: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    token: null,
    isAuthenticated: false
  })
  const [isLoading, setIsLoading] = useState(true)

  // Check for existing session on mount and VERIFY with backend
  useEffect(() => {
    const verifySession = async () => {
      const session = authService.getSession()
      
      if (session) {
        // Verify token with backend
        const verifiedUser = await authService.verifyToken(session.token)
        
        if (verifiedUser) {
          setAuthState({
            user: verifiedUser,
            token: session.token,
            isAuthenticated: true
          })
        } else {
          // Token invalid - clear session
          authService.logout()
          setAuthState({
            user: null,
            token: null,
            isAuthenticated: false
          })
        }
      }
      
      setIsLoading(false)
    }

    verifySession()
  }, [])

  const login = async (email: string, password: string) => {
    const { user, token } = await authService.login(email, password)
    setAuthState({
      user,
      token,
      isAuthenticated: true
    })
  }

  const logout = () => {
    authService.logout()
    setAuthState({
      user: null,
      token: null,
      isAuthenticated: false
    })
  }

  const hasRole = (roles: UserRole[]) => {
    return authService.hasRole(authState.user, roles)
  }

  return (
    <AuthContext.Provider value={{ ...authState, login, logout, hasRole, isLoading }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
